/**
 * Budgets — actual against budget, per canonical line, for one entity over a range of months,
 * and a form to set a month's budget. Phase 5, FINANCE.md §10.
 *
 * Every figure comes from `GET /api/finance/budget`: the actuals are the P&L's own rows, the
 * budget is `core.budget_line`, and `budgetVariance` subtracts one from the other in the
 * statement's signs, so a positive variance is favourable for every line — the screen colours
 * it and adds nothing up. The form takes dollars as text and converts them without a float;
 * expense lines are typed as positive dollars and stored negative, the convention the statement
 * engine uses, so the owner never types a minus sign to budget an expense.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dollarsToCents } from '@sixgen/core';
import {
  api,
  type BudgetResponse,
  type FinanceNarrationResponse,
  type Property,
} from '../../api.js';
import { NarrationCard } from '../../components/NarrationCard.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';
import { formatMoney } from '../../money.js';

const SECTION_LABEL: Record<string, string> = {
  operating_revenue: 'Operating revenue',
  operating_expense: 'Operating expense',
  debt_service: 'Debt service',
  capex: 'Capital expenditure',
  non_operating: 'Non-operating',
  equity_movement: 'Equity movement',
  balance_sheet: 'Balance sheet',
};

/** Sections whose amounts are money going out: typed positive, stored negative. */
const OUTFLOW_SECTIONS = new Set(['operating_expense', 'debt_service', 'capex']);

function yearMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function monthsAgo(n: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return yearMonth(d);
}

export function BudgetsPage(): ReactNode {
  const entityId = useEntityId();
  const entities = useEntities();
  const queryClient = useQueryClient();
  const [from, setFrom] = useState(() => monthsAgo(2));
  const [to, setTo] = useState(() => monthsAgo(0));
  const [budgetMonth, setBudgetMonth] = useState(() => monthsAgo(0));
  const [error, setError] = useState<string | null>(null);
  const [stored, setStored] = useState<string | null>(null);
  // One property's budget and actuals, or the entity-level ones. A budget line belongs to a
  // property or to none; the comparison follows the same choice.
  const [propertyId, setPropertyId] = useState<string>('');
  // The comparison's scope: this entity (with or without a property), every entity, or one
  // market's properties. The form always sets this entity's lines.
  const [scope, setScope] = useState<'entity' | 'portfolio' | 'market'>('entity');
  const [marketId, setMarketId] = useState<string>('');

  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<{ properties: Property[] }>('/api/properties'),
  });
  const markets = [
    ...new Map(
      (properties.data?.properties ?? []).map((p) => [p.market_id, p.market_name] as const),
    ).entries(),
  ];
  const propertyQuery = propertyId === '' ? '' : `&property=${propertyId}`;
  const scopeQuery =
    scope === 'portfolio'
      ? 'scope=portfolio'
      : scope === 'market'
        ? `scope=market&market=${marketId}`
        : `entity=${entityId}${propertyQuery}`;

  const report = useQuery({
    queryKey: ['finance', 'budget', scope, entityId, marketId, from, to, propertyId],
    enabled:
      from <= to &&
      (scope === 'portfolio' || (scope === 'market' ? marketId !== '' : entityId !== null)),
    queryFn: () =>
      api.get<BudgetResponse>(`/api/finance/budget?${scopeQuery}&from=${from}&to=${to}`),
  });

  const setBudget = useMutation({
    mutationFn: (body: unknown) => api.put<{ lines: unknown[] }>('/api/finance/budget', body),
    onSuccess: (result) => {
      setError(null);
      setStored(
        `Saved ${result.lines.length} budget ${result.lines.length === 1 ? 'line' : 'lines'} for ${budgetMonth}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['finance', 'budget'] });
    },
    onError: (err: unknown) => {
      setStored(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  function submit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!entityId || !report.data) return;
    const data = new FormData(event.currentTarget);
    try {
      const lines = report.data.canonical_keys.flatMap((k) => {
        const typed = String(data.get(`amount:${k.key}`) ?? '').trim();
        if (typed === '') return [];
        const cents = dollarsToCents(typed);
        const signed = OUTFLOW_SECTIONS.has(k.statement_section) ? -cents : cents;
        return [{ canonicalKey: k.key, amountCents: signed.toString() }];
      });
      if (lines.length === 0) {
        setError('Type a dollar amount on at least one line.');
        return;
      }
      setBudget.mutate({
        entityId,
        propertyId: propertyId === '' ? null : propertyId,
        month: budgetMonth,
        lines,
      });
    } catch (err) {
      setStored(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  // The Finance Agent's variance narration for the last completed month, entity level only:
  // the narration compares entity-level lines, as the portfolio scope does.
  const narration = useQuery({
    queryKey: ['finance', 'narration', entityId],
    enabled: entityId !== null && scope === 'entity' && propertyId === '',
    queryFn: () => api.get<FinanceNarrationResponse>(`/api/finance/narration?entity=${entityId}`),
  });

  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;
  const data = report.data;
  const existing = new Map(
    (data?.budget_lines ?? [])
      .filter((b) => b.month.slice(0, 7) === budgetMonth)
      .map((b) => [b.canonical_key, b.amount_cents]),
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Budgets</h1>
          <p className="subtitle">
            Actual against budget, line by line. Code subtracts; a positive variance is favourable
            on every line. The explanation is the Finance Agent&apos;s, below the comparison.
          </p>
        </div>
      </header>

      <div className="filters">
        <label>
          Compare{' '}
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as 'entity' | 'portfolio' | 'market')}
          >
            <option value="entity">One entity</option>
            <option value="market">One market</option>
            <option value="portfolio">Whole portfolio</option>
          </select>
        </label>
        {scope === 'entity' && <EntityPicker />}
        {scope === 'market' && (
          <label>
            Market{' '}
            <select value={marketId} onChange={(e) => setMarketId(e.target.value)}>
              <option value="">Choose a market</option>
              {markets.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          From{' '}
          <input type="month" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To <input type="month" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label hidden={scope !== 'entity'}>
          Property{' '}
          <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
            <option value="">Entity level</option>
            {(properties.data?.properties ?? [])
              .filter((p) => p.entity_id === entityId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
      </div>

      {(report.isLoading || (entityId === null && entities.isLoading)) && (
        <p className="muted">Loading…</p>
      )}
      {report.isError && <p className="error">Could not load budgets: {report.error.message}</p>}
      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {data && (
        <section className="card">
          <h2>
            Actual vs budget, {data.from} to {data.to}
            {data.scope === 'portfolio' && ' · whole portfolio'}
            {data.scope === 'market' && ' · one market'}
          </h2>
          {data.scope_note && <p className="muted small">{data.scope_note}</p>}
          {data.total.lines.length === 0 ? (
            <p className="muted">
              Nothing to compare yet: no mapped actuals and no budget lines in these months. Set a
              month&apos;s budget below.
            </p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th className="num">Budget</th>
                    <th className="num">Actual</th>
                    <th className="num">Variance</th>
                    <th className="num">%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.total.sections
                    .filter((s) => data.total.lines.some((l) => l.section === s.section))
                    .map((s) => (
                      <SectionRows key={s.section} section={s} lines={data.total.lines} />
                    ))}
                  <tr className="pnl-figure pnl-net">
                    <th scope="row">NOI</th>
                    <td className="num">{formatMoney(data.total.noi.budget_cents)}</td>
                    <td className="num">{formatMoney(data.total.noi.actual_cents)}</td>
                    <td className="num">
                      <Variance cents={data.total.noi.variance_cents} />
                    </td>
                    <td className="num">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <p className="muted small">
            {data.total.label} · {data.total.unbudgeted_lines} line
            {data.total.unbudgeted_lines === 1 ? '' : 's'} with actuals and no budget,{' '}
            {data.total.unspent_lines} with a budget and nothing actual · {data.total.calc_method}
          </p>
        </section>
      )}

      {data && entityId && scope === 'entity' && propertyId === '' && (
        <NarrationCard
          title="The variances, in the Finance Agent’s words"
          narration={narration.data?.narration}
          items={(narration.data?.narration?.items ?? []).filter((i) => i.kind === 'variance')}
          quiet="The last narration named no variance for this entity: no entity-level budget for the last completed month, or every line on budget."
        />
      )}

      {data && entityId && scope === 'entity' && (
        <section className="card">
          <h2>Set the budget for a month</h2>
          <p className="muted small">
            {propertyId === '' ? 'Entity-level lines' : 'This property\u2019s lines'}. Type dollars;
            expense, debt-service and CapEx lines are typed as positive amounts and stored in the
            statement&apos;s signs. A blank line is left as it is; an amount replaces the existing
            one, audited.
          </p>
          <form className="stack" onSubmit={submit}>
            <label>
              Month{' '}
              <input
                type="month"
                value={budgetMonth}
                onChange={(e) => setBudgetMonth(e.target.value)}
                required
              />
            </label>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Section</th>
                    <th className="num">Current budget</th>
                    <th className="num">New amount ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.canonical_keys
                    .filter((k) => k.statement_section !== 'balance_sheet')
                    .map((k) => (
                      <tr key={k.key}>
                        <td>
                          {k.name} <code className="muted small">{k.key}</code>
                        </td>
                        <td className="muted">
                          {SECTION_LABEL[k.statement_section] ?? k.statement_section}
                        </td>
                        <td className="num">
                          {existing.has(k.key) ? (
                            formatMoney(existing.get(k.key)!)
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td className="num">
                          <input name={`amount:${k.key}`} inputMode="decimal" placeholder="" />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div>
              <button type="submit" disabled={setBudget.isPending}>
                {setBudget.isPending ? 'Saving…' : 'Save budget lines'}
              </button>
            </div>
          </form>
          {stored && <p className="muted">{stored}</p>}
          {error && <p className="error">{error}</p>}
        </section>
      )}
    </div>
  );
}

function Variance({ cents }: { cents: string }): ReactNode {
  const negative = cents.startsWith('-');
  return (
    <span className={negative ? 'chip chip-attention' : cents === '0' ? '' : 'chip chip-ok'}>
      {formatMoney(cents)}
    </span>
  );
}

function SectionRows({
  section,
  lines,
}: {
  section: BudgetResponse['total']['sections'][number];
  lines: BudgetResponse['total']['lines'];
}): ReactNode {
  return (
    <>
      <tr className="pnl-section">
        <th scope="rowgroup" colSpan={5}>
          {SECTION_LABEL[section.section] ?? section.section}
        </th>
      </tr>
      {lines
        .filter((l) => l.section === section.section)
        .map((l) => (
          <tr key={l.key}>
            <td>
              <code>{l.key}</code>
              {!l.has_budget && <span className="chip-tag">no budget</span>}
              {!l.has_actual && <span className="chip-tag">nothing yet</span>}
            </td>
            <td className="num">{formatMoney(l.budget_cents)}</td>
            <td className="num">{formatMoney(l.actual_cents)}</td>
            <td className="num">
              <Variance cents={l.variance_cents} />
            </td>
            <td className="num">
              {l.variance_ratio === null ? '—' : `${(l.variance_ratio * 100).toFixed(0)}%`}
            </td>
          </tr>
        ))}
      <tr className="pnl-subtotal">
        <th scope="row">{SECTION_LABEL[section.section] ?? section.section} total</th>
        <td className="num">{formatMoney(section.budget_cents)}</td>
        <td className="num">{formatMoney(section.actual_cents)}</td>
        <td className="num">
          <Variance cents={section.variance_cents} />
        </td>
        <td className="num">—</td>
      </tr>
    </>
  );
}
